import { cn } from "../../utils/lib";

type ButtonVariant = "contained" | "outlined" | "text";
type ButtonSize = "sm" | "md" | "lg";
type ButtonColor = "primary" | "gray" | "green" | "red" | "yellow" | "blue";

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  color?: ButtonColor;
  text?: any;
  className?: string;
  leftIcon?: any;
  rightIcon?: any;
  isLoading?: boolean;
  disabled?: boolean;
  isFullWidth?: boolean;
  form?: string;
  onClick?: any;
  type?: "button" | "submit" | "reset";
  rounded?: "full" | "md" | "sm" | "lg";
}

const Button = ({
  type = "button",
  variant = "contained",
  size = "md",
  color = "primary",
  text,
  className = "",
  form,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  disabled = false,
  isFullWidth = false,
  isLoading = false,
  onClick,
  rounded = "md",
}: ButtonProps) => {
  const baseStyles =
    "inline-flex items-center text-nowrap group justify-center font-medium transition-all duration-200 ease-in-out";

  const roundedStyles = {
    full: "rounded-full",
    md: "rounded-lg",
    sm: "rounded-md",
    lg: "rounded-xl",
  };

  const colorStyles: Record<
    ButtonColor,
    {
      base: string;
      outlined: string;
      text: string;
    }
  > = {
    primary: {
      base: "bg-primary text-white hover:bg-indigo-700 border border-primary focus:outline-primary",
      outlined:
        "hover:border-primary text-primary hover:bg-indigo-50 border-primary focus:outline-primary",
      text: "text-primary hover:bg-indigo-50 border-0 focus:outline-primary",
    },
    gray: {
      base: "bg-gray-400 text-white hover:bg-gray-500 border border-gray-400 focus:outline-gray-400",
      outlined:
        "hover:border-gray-500 text-gray-500 hover:bg-gray-50 border-gray-200 focus:outline-gray-400",
      text: "text-gray-500 hover:bg-gray-50 border-0 focus:outline-gray-400",
    },
    green: {
      base: "bg-green-500 text-white hover:bg-green-600 border border-green-500 focus:outline-green-500",
      outlined:
        "hover:border-green-500 text-green-500 hover:bg-green-50 border-green-500 focus:outline-green-500",
      text: "text-green-500 hover:bg-green-50 border-0 focus:outline-green-500",
    },

    red: {
      base: "bg-red-500 text-white hover:bg-red-600 border border-red-500 focus:outline-red-500",
      outlined:
        "hover:border-red-500 text-red-500 hover:bg-red-50 border-red-500 focus:outline-red-500",
      text: "text-red-500 hover:bg-red-50 border-0 focus:outline-red-500",
    },
    yellow: {
      base: "bg-yellow-500 text-white hover:bg-yellow-600 border border-yellow-500 focus:outline-yellow-500",
      outlined:
        "hover:border-yellow-500 text-yellow-500 hover:bg-yellow-50 border-yellow-500 focus:outline-yellow-500",
      text: "text-yellow-500 hover:bg-yellow-50 border-0 focus:outline-yellow-500",
    },
    blue: {
      base: "bg-blue-500 text-white hover:bg-blue-600 border border-blue-500 focus:outline-blue-500",
      outlined:
        "hover:border-blue-500 text-blue-500 hover:bg-blue-50 border-blue-500 focus:outline-blue-500",
      text: "text-blue-500 hover:bg-blue-50 border-0 focus:outline-blue-500",
    },
  };

  const BUTTON_SIZE: { sm: string; md: string; lg: string } = {
    sm: "h-6 px-2 text-[10px] 2xl:h-7 2xl:px-2 2xl:text-xs",
    md: "px-3 py-2 text-xs 2xl:h-9 2xl:px-4 2xl:text-sm",
    lg: "h-10 px-4 text-base 2xl:h-11 2xl:px-5 2xl:text-lg",
  };

  const ICON_SIZE = {
    sm: "w-3 h-3",
    md: "w-3.5 h-3.5",
    lg: "w-4 h-4",
  };

  const getVariantStyles = (variant: ButtonVariant, color: ButtonColor) => {
    switch (variant) {
      case "contained":
        return `${colorStyles[color]?.base} border-transparent shadow`;
      case "outlined":
        return `${colorStyles[color]?.outlined} border bg-transparent`;
      case "text":
        return `${colorStyles[color]?.text} bg-transparent border-transparent`;
    }
  };

  const disableColor = disabled
    ? "cursor-not-allowed disabled:opacity-50"
    : "cursor-pointer";

  const fullWidth = isFullWidth ? "w-full" : "w-fit";

  const buttonClasses = cn(
    baseStyles,
    getVariantStyles(variant, color),
    BUTTON_SIZE[size],
    fullWidth,
    disableColor,
    className,
    roundedStyles[rounded],
  );

  return (
    <button
      className={buttonClasses}
      disabled={disabled}
      onClick={onClick}
      type={type}
      form={form}
    >
      {/* {isLoading && (
        <LoaderIcon className={cn("mr-1.5 animate-spin", ICON_SIZE[size])} />
      )} */}
      {!isLoading && LeftIcon && (
        <LeftIcon className={cn("mr-1.5", ICON_SIZE[size])} />
      )}
      <span
        className={
          "transition-all duration-200 ease-in-out group-active:scale-95"
        }
      >
        {text}
      </span>
      {!isLoading && RightIcon && (
        <RightIcon className={cn("ml-1.5", ICON_SIZE[size])} />
      )}
    </button>
  );
};

export { Button };
